<template>
	<header
		:class="{ 'site-header--stuck': isStuck }"
		class="site-header"
	>
		<div class="container site-header__inner">
			<a
				class="site-header__brand"
				href="#top"
				aria-label="Beetronix"
			>
				<!-- the lockup already carries the company name, so no text beside it -->
				<img
					:src="assetUrl('logo-full.png')"
					class="site-header__logo"
					alt="Beetronix"
					width="689"
					height="200"
				>
			</a>

			<nav
				id="site-nav"
				ref="navElement"
				:class="{ 'site-nav--open': isNavOpen }"
				:aria-label="t('general.menu')"
				class="site-nav"
				@click="closeNavOnLink"
			>
				<a
					v-for="id in SECTION_IDS"
					:key="id"
					:href="`#${id}`"
					:class="{ 'site-nav__link--active': activeSection === id }"
					class="site-nav__link"
				>{{ t(`nav.${id}`) }}</a>
			</nav>

			<div class="site-header__actions">
				<button
					:aria-label="t('general.switch-language-label')"
					class="lang-switch"
					type="button"
					@click="toggleLanguage"
				>
					<app-icon
						:size="15"
						:stroke-width="1.6"
						name="globe"
					/>
					<span class="lang-switch__label">{{ t('general.switch-language') }}</span>
				</button>

				<a
					:href="ownProfile.href"
					:download="ownProfile.name"
					class="btn btn--gold btn--sm site-header__download"
				>
					<app-icon name="download" />
					<span class="site-header__download-label">
						{{ t('general.download-profile') }}
					</span>
				</a>

				<button
					:class="{ 'nav-toggle--open': isNavOpen }"
					:aria-expanded="String(isNavOpen)"
					:aria-label="t('general.menu')"
					class="nav-toggle"
					type="button"
					aria-controls="site-nav"
					@click.stop="isNavOpen = !isNavOpen"
				>
					<span class="nav-toggle__bar" />
					<span class="nav-toggle__bar" />
					<span class="nav-toggle__bar" />
				</button>
			</div>
		</div>
	</header>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AppIcon from '@/components/AppIcon.vue'
import { PROFILE_FILES, SECTION_IDS } from '@/helpers/constants'
import { assetUrl } from '@/helpers/functions'
import { toggleLanguage } from '@/i18n'

defineOptions({ name: 'SiteHeader' })

const { t, locale } = useI18n()

const ownProfile = computed(() => PROFILE_FILES[locale.value] || PROFILE_FILES.en)

/* Sticky shadow */
const isStuck = ref(false)

const handleScroll = () => {
	isStuck.value = window.scrollY > 8
}

/* Mobile drawer */
const isNavOpen = ref(false)
const navElement = ref(null)

const closeNavOnLink = (event) => {
	if (event.target.closest('a')) isNavOpen.value = false
}

const handleKeydown = (event) => {
	if (event.key === 'Escape') isNavOpen.value = false
}

const handleOutsideClick = (event) => {
	if (!isNavOpen.value) return
	if (!event.target.closest('#site-nav, .nav-toggle')) isNavOpen.value = false
}

/* Active section highlight */
const activeSection = ref('')
let sectionObserver = null

const observeSections = () => {
	if (!('IntersectionObserver' in window)) return

	sectionObserver = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) activeSection.value = entry.target.id
			})
		},
		{ rootMargin: '-45% 0px -50% 0px' }
	)

	SECTION_IDS.forEach((id) => {
		const section = document.getElementById(id)
		if (section) sectionObserver.observe(section)
	})
}

onMounted(() => {
	handleScroll()
	window.addEventListener('scroll', handleScroll, { passive: true })
	document.addEventListener('keydown', handleKeydown)
	document.addEventListener('click', handleOutsideClick)
	observeSections()
})

onBeforeUnmount(() => {
	window.removeEventListener('scroll', handleScroll)
	document.removeEventListener('keydown', handleKeydown)
	document.removeEventListener('click', handleOutsideClick)
	if (sectionObserver) sectionObserver.disconnect()
})
</script>

<style scoped lang="scss">
.site-header {
	position: fixed;
	top: 0;
	inset-inline: 0;
	z-index: 100;
	height: var(--header-h);
	background: rgba(255, 255, 255, 0.82);
	backdrop-filter: blur(14px) saturate(160%);
	-webkit-backdrop-filter: blur(14px) saturate(160%);
	border-bottom: 1px solid transparent;
	transition: box-shadow 0.3s, border-color 0.3s;
}

.site-header--stuck {
	border-bottom-color: $color-line;
	box-shadow: 0 6px 24px rgba(13, 13, 13, 0.06);
}

.site-header__inner {
	display: flex;
	align-items: center;
	gap: clamp(12px, 2vw, 32px);
	height: 100%;
}

.site-header__brand {
	display: flex;
	align-items: center;
	flex: none;
}

.site-header__logo {
	height: clamp(30px, 3.4vw, 40px);
	width: auto;
}

.site-nav {
	display: flex;
	gap: clamp(10px, 1.5vw, 24px);
	margin-inline-start: auto;
}

.site-nav__link {
	position: relative;
	padding: 6px 2px;
	font-size: 0.9rem;
	font-weight: 500;
	color: #4a4a4a;
	white-space: nowrap;
	transition: color 0.2s;

	&::after {
		content: '';
		position: absolute;
		bottom: 0;
		inset-inline-start: 0;
		height: 2px;
		width: 0;
		background: $color-gold;
		transition: width 0.28s $ease;
	}

	&:hover {
		color: $color-ink;
	}

	&:hover::after {
		width: 100%;
	}
}

.site-nav__link--active {
	color: $color-ink;

	&::after {
		width: 100%;
	}
}

.site-header__actions {
	display: flex;
	align-items: center;
	gap: 10px;
	flex: none;

	// the language switch and the download button are siblings in the bar, so
	// they share one height and one horizontal rhythm
	.lang-switch,
	.btn {
		height: $control-height;
		padding: 0 18px;
		font-size: 0.86rem;
	}
}

.lang-switch {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	background: none;
	border: 1.5px solid $color-line-strong;
	border-radius: 999px;
	font-weight: 600;
	color: $color-ink;
	cursor: pointer;
	transition: border-color 0.2s, background 0.2s;

	&:hover {
		border-color: $color-ink;
		background: rgba(13, 13, 13, 0.04);
	}
}

.nav-toggle {
	display: none;
	width: 42px;
	height: 42px;
	padding: 10px;
	background: none;
	border: 0;
	cursor: pointer;
}

.nav-toggle__bar {
	display: block;
	height: 2px;
	border-radius: 2px;
	background: $color-ink;
	transition: transform 0.3s $ease, opacity 0.2s;

	& + & {
		margin-top: 5px;
	}
}

.nav-toggle--open {
	.nav-toggle__bar:nth-child(1) {
		transform: translateY(7px) rotate(45deg);
	}

	.nav-toggle__bar:nth-child(2) {
		opacity: 0;
	}

	.nav-toggle__bar:nth-child(3) {
		transform: translateY(-7px) rotate(-45deg);
	}
}

@include below($bp-desktop) {
	.site-nav {
		position: fixed;
		inset: var(--header-h) 0 auto 0;
		flex-direction: column;
		gap: 0;
		margin-inline-start: 0;
		padding: 8px clamp(18px, 4vw, 40px) 20px;
		background: rgba(255, 255, 255, 0.97);
		backdrop-filter: blur(16px);
		-webkit-backdrop-filter: blur(16px);
		border-bottom: 1px solid $color-line;
		box-shadow: 0 18px 40px rgba(13, 13, 13, 0.1);
		transform: translateY(-12px);
		opacity: 0;
		visibility: hidden;
		transition: transform 0.3s $ease, opacity 0.25s, visibility 0.25s;
	}

	.site-nav--open {
		transform: none;
		opacity: 1;
		visibility: visible;
	}

	.site-nav__link {
		padding: 14px 2px;
		font-size: 1rem;
		border-bottom: 1px solid $color-line;

		&::after {
			display: none;
		}
	}

	.nav-toggle {
		display: block;
	}

	.site-header__actions {
		margin-inline-start: auto;
	}
}

// below the tablet break both controls collapse to equal square icon buttons
@include below($bp-tablet) {
	.site-header__download-label,
	.lang-switch__label {
		display: none;
	}

	.site-header__actions {
		.lang-switch,
		.btn {
			width: $control-height;
			padding: 0;
		}
	}
}
</style>
